import styled from "styled-components";
import {
    ICountryData,
    IHandleBlur,
    IHandleChange,
    IHandleSubmit,
    ISetCustomerData,
} from "@interfaces";
import { InputCommon, SelectCommon, StyledError } from "@components";
import { SeparateDatePicker } from "../Common/SeparateDatePicker";
import { breakpoint, theme } from "@utilities";
import { useField, useFormikContext } from "formik";
import { TextField } from "@mui/material";
import { toCountryOptions } from "src/Utilities/countryOrder";
interface IProps {
    values: ISetCustomerData;
    handleChange: IHandleChange;
    handleBlur: IHandleBlur;
    handleSubmit: IHandleSubmit;
    dateValue: Date | null | undefined;
    countriesData: ICountryData[];
    handleChangeDate: (date: Date | null, value?: string | null | undefined) => void;
    isLeader?: boolean;
    isShowErrorDate: boolean;
}

export const AddTravellerFormComponent = (props: IProps) => {
    const {
        values,
        handleChange,
        handleBlur,
        handleSubmit,
        dateValue,
        countriesData,
        handleChangeDate,
    } = props;

    const countriesModel = toCountryOptions(countriesData);
    const { setFieldValue, setFieldTouched, errors, touched } =
        useFormikContext<ISetCustomerData>();
    const [field] = useField(`countryCode`);
    const [titleField] = useField(`title`);

    const TitleModel = [
        { id: "Mr", label: "Mr" },
        { id: "Mrs", label: "Mrs" },
        { id: "Miss", label: "Miss" },
        { id: "Ms", label: "Ms" },
        { id: "Mx", label: "Mx" },
        { id: "Master", label: "Master" },
    ];

    const raw = values.phoneNumber ?? "";
    const digitCount = (raw.match(/\d/g) || []).length;
    const isTooShort = digitCount > 0 && digitCount < 4;

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.ctrlKey || e.metaKey) return;
        if (["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) return;
        if (!/[\d()+\-\s]/.test(e.key)) e.preventDefault();
    };
    const sanitize = (str: string) => str.replace(/[^0-9()+\-\s]/g, "");

    const handlePhonePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        window.requestAnimationFrame(() => {
            const pasted = (e.target as HTMLInputElement).value;
            handleChange(sanitize(pasted));
        });
    };

    const calculateAge = (dob: Date | null | undefined): number | null => {
        if (!dob) return null;
        const today = new Date();
        let age = today.getFullYear() - dob.getFullYear();
        const monthDiff = today.getMonth() - dob.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
            age--;
        }
        return age >= 0 ? age : null;
    };

    const age = calculateAge(dateValue);

    return (
        <Container onSubmit={handleSubmit}>
            <SelectCommon
                name={`title`}
                handleChange={(value) => setFieldValue(`title`, value)}
                handleBlur={handleBlur}
                value={titleField.value}
                label="Title"
                required
                listValueMembers={TitleModel}
            />
            <div className="nameWrapper">
                <div className="firstName">
                    <InputCommon
                        name={`firstName`}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        value={values.firstName}
                        label="First name *"
                        type="text"
                        required
                    />
                </div>
                <div className="lastName">
                    <InputCommon
                        name={`lastName`}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        required
                        value={values.lastName}
                        label="Last name *"
                        type="text"
                    />
                </div>
            </div>
            <InputCommon
                name={`email`}
                handleChange={handleChange}
                handleBlur={handleBlur}
                value={values.email}
                label="Email *"
                required
                type="text"
            />
            <TextField
                name="phoneNumber"
                label="Phone"
                type="tel"
                required
                placeholder="+1 (234) 567-8900"
                fullWidth
                variant="standard"
                sx={{ marginTop: 2 }}
                value={raw}
                onKeyDown={handleKeyDown}
                onChange={handleChange}
                onBlur={handleBlur}
                slotProps={{
                    htmlInput: {
                        inputMode: "tel",
                        pattern: `(?=(?:.*\\d){4,})[0-9()+\\-\\s]+`,
                        title: "Enter at least 4 digits; only digits, spaces, (), +, - allowed",
                        onPaste: handlePhonePaste,
                    },
                    inputLabel: {
                        sx: {
                            "&.MuiInputLabel-shrink": {
                                fontWeight: "bold",
                                color: theme.colors.highlight,
                            },
                        },
                    },
                }}
            />
            {touched.phoneNumber && (isTooShort || errors.phoneNumber) && (
                <StyledError>
                    {isTooShort ? "Please enter at least 4 digits." : errors.phoneNumber}
                </StyledError>
            )}
            <div className="dobWrapper">
                <SeparateDatePicker
                    value={dateValue}
                    onChange={(date: Date | null) => {
                        handleChangeDate(date);
                        setFieldTouched("dateOfBirth", true);
                    }}
                    label="Date of birth *"
                    name="dateOfBirth"
                    handleBlur={() => setFieldTouched("dateOfBirth", true)}
                    maxDate={new Date(new Date().getFullYear() - 18, new Date().getMonth(), new Date().getDate())}
                    minDate={new Date("1/1/1900")}
                />
                {age !== null && <span className="ageLabel">Age: {age}</span>}
            </div>
            {touched.dateOfBirth && errors.dateOfBirth && (
                <StyledError>{errors.dateOfBirth}</StyledError>
            )}
            <InputCommon
                name={`postalCode`}
                handleChange={handleChange}
                handleBlur={handleBlur}
                value={values.postalCode}
                label="Post code *"
                required
                type="text"
            />

            <SelectCommon
                name={`countryCode`}
                handleChange={(value) => setFieldValue(`countryCode`, value)}
                handleBlur={handleBlur}
                value={field.value}
                label="Country"
                required
                listValueMembers={countriesModel}
                openOnTop
            />
        </Container>
    );
};

const Container = styled.form`
    .nameWrapper {
        display: flex;
        margin-bottom: 20px;
        width: 100%;
        justify-content: space-between;

        ${breakpoint.breakOnlyMobile`
            display : flex;
            flex-direction : column;
        `}
        .firstName {
            margin-right: 16px;
            width: 100%;
            ${breakpoint.breakOnlyMobile`
                margin-right: 0px;
            `}
        }
        .lastName {
            width: 100%;
        }
    }
    .dobWrapper {
        display: flex;
        align-items: center;
        gap: 12px;
        .ageLabel {
            font-size: 14px;
            font-weight: 600;
            color: ${theme.colors.highlight};
            white-space: nowrap;
            padding-top: 16px;
        }
    }
`;
